import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Typography,
    Box,
    Chip,
    Alert,
} from '@mui/material';
import {
    getSuggestedAttribution,
    setManualAttribution,
    SuggestedAttribution,
} from '../../api/chatApi';
import logger from '../../utils/logger';

interface SuggestAttributionDialogProps {
    open: boolean;
    workerId: string;
    onClose: () => void;
    /** Called after the admin successfully picks a candidate. */
    onAttributed: () => void;
}

const scoreColor: Record<SuggestedAttribution['score'], 'success' | 'primary' | 'default'> = {
    high: 'success',
    medium: 'primary',
    low: 'default',
};

const SuggestAttributionDialog: React.FC<SuggestAttributionDialogProps> = ({
    open,
    workerId,
    onClose,
    onAttributed,
}) => {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [suggestions, setSuggestions] = useState<SuggestedAttribution[]>([]);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setSuggestions([]);
        getSuggestedAttribution(workerId)
            .then(rows => {
                if (cancelled) return;
                setSuggestions(rows);
            })
            .catch(err => {
                if (cancelled) return;
                logger.error('Failed to load attribution suggestions', err);
                setError('Could not load suggestions. Please try again.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, workerId]);

    const handlePick = async (referralLinkId: string) => {
        try {
            setSubmitting(referralLinkId);
            setError(null);
            await setManualAttribution(workerId, referralLinkId);
            onAttributed();
            onClose();
        } catch (err: any) {
            logger.error('Failed to set manual attribution', err);
            setError(err?.response?.data?.message ?? 'Could not attribute session. Please try again.');
        } finally {
            setSubmitting(null);
        }
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Suggest attribution</DialogTitle>
            <DialogContent>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}
                {!loading && error && <Alert severity="error">{error}</Alert>}
                {!loading && !error && suggestions.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        No plausible referral links found for this conversation. Likely organic —
                        leave unattributed or attribute via a different signal.
                    </Typography>
                )}
                {!loading && suggestions.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                        {suggestions.map(s => (
                            <Box
                                key={s.referralLinkId}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    p: 1.5,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                    <Typography variant="subtitle2">{s.referralLinkTitle}</Typography>
                                    <Chip size="small" color={scoreColor[s.score]} label={s.score.toUpperCase()} />
                                </Box>
                                {s.recruiterName && (
                                    <Typography variant="caption" color="text.secondary">
                                        Recruiter: {s.recruiterName}
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    {s.evidence}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={submitting !== null}
                                        onClick={() => handlePick(s.referralLinkId)}
                                    >
                                        {submitting === s.referralLinkId ? (
                                            <CircularProgress size={16} sx={{ color: 'inherit' }} />
                                        ) : (
                                            'Attribute to this link'
                                        )}
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting !== null}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SuggestAttributionDialog;
